import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  costFormater,
  Hint,
  Preloader,
  ResetIcon,
  Responsive,
  Text,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import routes from '@/common/routes';
import { ConfettiSuccessIcon } from '@/components';
import { SuccessModal } from '@/components/elements';
import { PreventLeaveModal } from '@/modules/core/components';
import { useHistory } from '@/modules/core/hooks';

import ServerErrorModal from '../../modal/ServerErrorModal';
import {
  useInvestmentApproveQuery,
  useInvestmentFundingQuery,
  useTradeInvestmentsQuery,
} from '../../queries';
import PersonalInfoModal from '../../StartInvesting/PersonalInfoModal';
import { useEmployeeData } from '../../StartInvesting/PersonalInfoModal/PersonalInfoForm/hooks';
import AddMoreFunds from '../AddMoreFunds';
import { useDefaultAllocationFunds } from '../hooks';
import { useSwitchInvestmentModelQuery } from '../queries';
import { useFundsList } from '../SelfDirectedFunds/hooks';
import { useSelectedFundsStore } from '../stores';

import ConfirmModal from './ConfirmModal';
import { useSelfDirectedAllocation } from './hooks';
import SelfDirectedAllocationHeader from './SelfDirectedAllocationHeader';
import SelfDirectedAllocationItem from './SelfDirectedAllocationItem';
import SelfDirectedFooter from './SelfDirectedFooter';
import TotalPercents from './TotalPercents';

const DEFAULT_PER_PAGE = 50;

interface Props {
  isEmptyPortfolioMode?: boolean;

  investAmount: number;
  accountId: string;
  investorId?: string;
  isInvestMoreMode?: boolean;
  isSwitchMode?: boolean;
  isErrors?: boolean;
  isSubmitted: boolean;
  onSetSubmitted: (value: boolean) => void;
  onConfirm?: () => void;
}
const MAX_PERCENTAGE = 100;
const SelfDirectedAllocationFunds: React.FC<Props> = ({
  isEmptyPortfolioMode,
  investAmount,
  investorId,
  onConfirm,
  isSwitchMode,
  isErrors,
  isInvestMoreMode,
  accountId,
  isSubmitted,
  onSetSubmitted,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isPreventLeave, setPreventLeave] = useState(true);
  const [isServerErrorModal, setServerErrorModal] = useState(false);
  const [isConfirmModal, setConfirmModal] = useState(false);
  const [isSuccessModal, setSuccessModal] = useState(false);
  const { page, perPage } = usePaginationParams(DEFAULT_PER_PAGE);
  const { data, isLoading } = useFundsList({
    accountId: accountId,
    isInvestMoreMode,
    isAllocationMode: true,
    isSwitchMode,
    searchParams: {
      page,
      perPage,
      sortBy: 'name,asc',
    },
  });

  const { defaultFundsIds } = useDefaultAllocationFunds();
  const selectedIds = useSelectedFundsStore((state) => state.selectedFunds);
  const setSelectedFunds = useSelectedFundsStore((state) => state.setSelectedFunds);

  useEffect(() => {
    if (defaultFundsIds && !selectedIds.length) {
      setSelectedFunds(defaultFundsIds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFundsIds]);

  useEffect(() => {
    if (isInvestMoreMode && !selectedIds.length && data) {
      const ids = data.map((item) => item.id);
      setSelectedFunds(ids);
    }
  }, [data, isInvestMoreMode, selectedIds.length, setSelectedFunds]);
  const currentSelectedIds = useMemo(() => {
    if (isInvestMoreMode && !selectedIds.length) {
      const ids = data.map((item) => item.id);
      return ids;
    }
    return selectedIds;
  }, [data, isInvestMoreMode, selectedIds]);
  const {
    key,
    checkedIds,
    filteredList,
    filteredListWithValues,
    values,
    handleChange,
    handleReset,
    totalPercents,
    handleCheckItem,
    stocksQuantities,
    percentsArray,
    errorText,
    amountValues,
    totalSelected,
  } = useSelfDirectedAllocation({
    data,
    investAmount,
    isInvestMoreMode,
    selectedIds: currentSelectedIds,
  });

  const history = useHistory();
  const handleBack = useCallback(() => {
    if (isInvestMoreMode) {
      history.goBack();
    } else if (isEmptyPortfolioMode) {
      history.push(ROUTES.SELF_DIRECTED_EMPTY_SWITCH_SELECT(accountId));
    } else if (isSwitchMode) {
      history.push(ROUTES.SELF_DIRECTED_SWITCH_SELECT(accountId));
    } else {
      history.push(`${ROUTES.SELF_DIRECTED(accountId)}?investorId=${investorId}`);
    }
  }, [accountId, history, investorId, isEmptyPortfolioMode, isInvestMoreMode, isSwitchMode]);
  const { handleSave: handleApproveFunding, isLoading: isApproveLoading } = useInvestmentApproveQuery();
  const { handleSave: handleSaveFunding, isLoading: isFundingLoading } = useInvestmentFundingQuery();

  const { handleSave: handleTrade, isLoading: isTradeLoading } = useTradeInvestmentsQuery({
    isSellMode: false,
  });
  const { handleSave: handleSwitchModel, isLoading: isSwitchLoading } = useSwitchInvestmentModelQuery(
    investorId || ''
  );

  // Personal info checker
  const { isFull, isLoading: isProfileLoading } = useEmployeeData();
  const [isPersonalInfoModalVisible, setPersonalInfoModalVisible] = useState(false);
  const [isPersonalInfoSuccessModalVisible, setIsPersonalInfoSuccessModalVisible] = useState(false);
  const [citizenshipRejected, setCitizenshipRejected] = useState(false);

  const handleNext = useCallback(async () => {
    if (!isFull) {
      setPersonalInfoModalVisible(true);
      return;
    }
    if (isFundingLoading || isTradeLoading || isApproveLoading || isSwitchLoading) {
      return;
    }
    onSetSubmitted(true);

    if (!errorText && !isErrors) {
      setPreventLeave(false);
      try {
        if (!isInvestMoreMode && !isSwitchMode) {
          await handleSaveFunding({
            amount: investAmount,
            amounts: amountValues,
          });
          setConfirmModal(true);
        } else {
          setConfirmModal(true);
        }
      } catch {
        setConfirmModal(false);
        setServerErrorModal(true);
      }
    }
  }, [
    isFull,
    amountValues,
    errorText,
    handleSaveFunding,
    investAmount,
    isApproveLoading,
    isErrors,
    isFundingLoading,
    isInvestMoreMode,
    isSwitchLoading,
    isSwitchMode,
    isTradeLoading,
    onSetSubmitted,
  ]);
  const handleApprove = useCallback(async () => {
    if (isApproveLoading) {
      return;
    }
    if (isSwitchMode) {
      try {
        await handleSwitchModel({
          preOrders: percentsArray,
          investmentAmount: isEmptyPortfolioMode ? investAmount : undefined,
        });
      } catch {
        setConfirmModal(false);
        setSuccessModal(false);
        setServerErrorModal(true);
        return;
      }
    } else if (isInvestMoreMode) {
      if (investorId) {
        try {
          await handleTrade({
            amount: investAmount,
            preOrders: stocksQuantities,
          });
        } catch {
          setConfirmModal(false);
          setSuccessModal(false);
          setServerErrorModal(true);
          return;
        }
      }
    } else {
      await handleApproveFunding(undefined);
    }
    setConfirmModal(false);
    setSuccessModal(true);
  }, [
    handleApproveFunding,
    handleSwitchModel,
    handleTrade,
    investAmount,
    investorId,
    isApproveLoading,
    isInvestMoreMode,
    isEmptyPortfolioMode,
    isSwitchMode,
    percentsArray,
    stocksQuantities,
  ]);

  const handleCloseSuccess = useCallback(() => {
    if (onConfirm) {
      onConfirm();
    }
    setSuccessModal(false);
    history.push(`${routes.INVESTMENTS}?id=${accountId}`);
  }, [accountId, history, onConfirm]);
  const buttonText = useMemo(() => {
    if (isSwitchMode) {
      return t('Submit (3/3)');
    }
    return t('Next (4/4)');
  }, [isSwitchMode, t]);
  if (isLoading) {
    return <Preloader />;
  }
  return (
    <>
      <ServerErrorModal
        visible={isServerErrorModal}
        onSetVisible={setServerErrorModal}
        onSendRequest={handleNext}
      />
      <PreventLeaveModal
        isPrevent={!!isSwitchMode && isPreventLeave}
        onConfirm={() => setSelectedFunds([])}
      />
      <SuccessModal
        testId="success-allocation"
        onSetVisible={handleCloseSuccess}
        icon={<ConfettiSuccessIcon />}
        buttonStyle={{
          zIndex: 100,
        }}
        buttonText={t('Go to Investment Dashboard')}
        helptext={
          isSwitchMode ? (
            <Box direction="column" align="center" justify="center">
              <Text textAlign="center" size="medium">
                {t('Updating your investment model will take 3 - 4 business days to complete.')}
              </Text>
              <Text textAlign="center" size="medium">
                {t(
                  'During that time you will not be able to make changes to your investment portfolio. This includes additional buys or sells.'
                )}
              </Text>
            </Box>
          ) : null
        }
        visible={isSuccessModal}
        onSubmit={handleCloseSuccess}
        header={t('Submitted!')}
      />
      <ConfirmModal
        isSwitchMode={isSwitchMode}
        visible={isConfirmModal}
        percentsMap={values}
        isInvestMoreMode={isInvestMoreMode}
        onSetVisible={setConfirmModal}
        list={filteredListWithValues}
        onSubmit={handleApprove}
        isLoading={isApproveLoading || isFundingLoading || isTradeLoading || isSwitchLoading}
        investedAmount={investAmount}
      />

      <Box direction="column" data-testid="SelfDirectedAllocation">
        {!isSwitchMode && (
          <Text
            size="xxlarge"
            data-testid="SelfDirectedAllocation_title"
            weight={700}
            margin={isMobile ? { horizontal: 'spacing16', vertical: 'spacing12' } : { bottom: 'spacing18' }}
          >
            {t('Purchase Allocation')}
          </Text>
        )}
        <Box pad={isMobile ? 'spacing16' : 'spacing24'} background="module" round="moduleRound">
          <Box margin={isMobile ? {} : { bottom: 'spacing12' }} direction="row" justify="between">
            <Text size="large" margin={{ bottom: 'spacing8', top: 'spacing8' }} color="textBody" weight={700}>
              {isInvestMoreMode ? t('Funds') : t('Selected funds')}: {totalSelected}
            </Text>
            <AddMoreFunds excludeIds={currentSelectedIds} accountId={accountId} />
          </Box>
          <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
            <SelfDirectedAllocationHeader onReset={handleReset} />
          </Responsive>
          <Box key={key}>
            {filteredList.map((item) => {
              return (
                <SelfDirectedAllocationItem
                  key={`${item.id} ${!investAmount}`}
                  // isOnlyOneAvailable={isOnlyOneAvailable}
                  value={values[item.id]}
                  status={item.status}
                  isInvestMoreMode={isInvestMoreMode}
                  investedAmount={investAmount}
                  onChangeValue={handleChange(item.id)}
                  disabledAllocation={!investAmount}
                  disabled={!investAmount || !checkedIds.includes(item.id)}
                  checked={checkedIds.includes(item.id)}
                  {...item}
                  onSelect={handleCheckItem}
                />
              );
            })}
          </Box>
          <Box
            direction="row"
            pad={isMobile ? 'spacing12' : { vertical: '6px', horizontal: 'spacing24' }}
            background="border1"
            margin={{ top: 'spacing8' }}
            round="container2Round"
            justify="between"
          >
            <Text size={isMobile ? 'large' : 'medium'} style={isMobile ? { flex: 1 } : {}} weight={700}>
              {t('Total')}
            </Text>
            <Box direction="row">
              <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
                <Box width="90px">
                  <Text size="medium" weight={700} data-testid="SelfDirectedAllocation_total-amount">
                    {costFormater(investAmount * (totalPercents / 100), {
                      isPrecision: true,
                    })}
                  </Text>
                </Box>
              </Responsive>
              <Box
                direction="row"
                align="center"
                justify="center"
                style={isMobile ? { flex: 1 } : {}}
                width="176px"
              >
                {totalPercents !== MAX_PERCENTAGE ? (
                  <Hint hintElement={<TotalPercents value={investAmount ? totalPercents : 0} />}>
                    <Text
                      size={isMobile ? 'large' : 'medium'}
                      data-testid="SelfDirectedAllocation_error"
                      textAlign="center"
                      margin={{ horizontal: 'xs' }}
                    >
                      {totalPercents > MAX_PERCENTAGE
                        ? t('The total percentage cannot exceed 100%.')
                        : t('The total percentage must equal 100%.')}
                    </Text>
                  </Hint>
                ) : (
                  <TotalPercents
                    size={isMobile ? 'large' : 'medium'}
                    value={investAmount ? totalPercents : 0}
                  />
                )}
              </Box>
            </Box>

            <Responsive size={Responsive.Mobile}>
              <Box width="90px" style={{ flex: 1 }}>
                <Text
                  size="medium"
                  textAlign="end"
                  weight={700}
                  data-testid="SelfDirectedAllocation_total-amount"
                >
                  {costFormater(investAmount * (totalPercents / 100), {
                    isPrecision: true,
                  })}
                </Text>
              </Box>
            </Responsive>
          </Box>

          <Responsive size={Responsive.Mobile}>
            <Box margin={{ top: 'spacing12' }}>
              <AppButton
                buttonType="secondary"
                fill
                rightIcon={<ResetIcon size="24px" color="iconPrimary" />}
                onClick={handleReset}
              >
                {t('Auto-election')}
              </AppButton>
            </Box>
          </Responsive>

          <Box margin={{ top: 'spacing12' }}>
            <Text size="medium" color="textSecondary">
              {t(
                '*Actual share price and number of shares will be calculated at the time the trade is executed.'
              )}
            </Text>
          </Box>
        </Box>
        <Box direction="column">
          {((!isFull && !isProfileLoading) || isPersonalInfoSuccessModalVisible) && (
            <Box margin={{ top: 'spacing24' }}>
              <PersonalInfoModal
                visible={isPersonalInfoModalVisible}
                onSetVisible={setPersonalInfoModalVisible}
                onResetRejectByCitizenship={() => setCitizenshipRejected(false)}
                onRejectByCitizenship={() => setCitizenshipRejected(true)}
                onSetVisibleSuccessModal={setIsPersonalInfoSuccessModalVisible}
              />
            </Box>
          )}
          <Box direction={isMobile ? 'column' : 'row'} justify="end">
            <Box direction="column" margin={{ horizontal: 'spacing16' }}>
              {isSubmitted && !!errorText ? (
                <Text color="danger" margin={{ bottom: 'spacing12' }}>
                  {errorText}
                </Text>
              ) : null}
              <Box margin={{ vertical: 'spacing16' }}>
                {isInvestMoreMode ? (
                  <Box direction="row" gap="spacing8" justify="end">
                    {!isMobile && (
                      <AppButton
                        testId="investments_back"
                        buttonType="secondary"
                        width="140px"
                        onClick={handleBack}
                      >
                        {t('Back')}
                      </AppButton>
                    )}

                    <AppButton
                      fill={isMobile}
                      disabled={(isSubmitted && (!!errorText || isErrors)) || citizenshipRejected}
                      width="140px"
                      testId="investments_next"
                      onClick={handleNext}
                    >
                      {t('Invest')}
                    </AppButton>
                  </Box>
                ) : (
                  <Box direction="row" gap="spacing8" justify="end">
                    <Box fill={isMobile}>
                      <AppButton
                        fill={isMobile}
                        testId="investments_back"
                        buttonType="secondary"
                        width="140px"
                        onClick={handleBack}
                      >
                        {t('Back')}
                      </AppButton>
                    </Box>
                    <Box fill={isMobile}>
                      <AppButton
                        fill={isMobile}
                        disabled={(isSubmitted && (!!errorText || isErrors)) || isFundingLoading}
                        width="140px"
                        testId="investments_next"
                        onClick={handleNext}
                      >
                        {buttonText}
                      </AppButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <SelfDirectedFooter />
        </Box>
      </Box>
    </>
  );
};
export default SelfDirectedAllocationFunds;
